import { useEffect, useRef, useState, useContext } from 'react';
import SimpleBar from 'simplebar-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Toggle } from 'thrive-components'
import Calendar from 'react-calendar'

// hooks and services
import useDetectClick from  '@/components/hooks/useDetectClick';
import useHTTP from  '@/components/hooks/useHTTP';
import { PlannerContext } from '@/context/PlannerContext';

//components
import InputCount from './InputCount';
import InviteesAdd from './InviteesAdd';
import EventTime from './EventTme';
import ModalWrapper from  '@/components/modals/ModalWrapper';
import InviteesViewEdit from './InviteesViewEdit';
import ToolTipWrapper from '../Tooltip/ToolTipWrapper';
import { IconLibrary, ThriveIcon } from 'thrive-components';

//interfaces
import { WorkflowType, group, occurrenceType, plannerEvent, plannerEventCount, user } from  '@/interfaces/planner';
import { ToastData } from  '@/interfaces/LockerDetails';
import { ToastMessageType } from '../global/ToastNotification';

// utils
import { isValidStartEndTime } from  '@/helpers/Planner';
import { datesSame, padZeros } from  '@/helpers/Dates';

// styles
import '../../../css/addEvent.scss'
import {AnimatePresence, motion} from "framer-motion";


interface AddEventProps {
    plusDate?:Date|null,
    closeModal:() => void,
    setToast:(toastData:ToastData) => void,
    eventCount?:plannerEventCount,
    event?:plannerEvent,
}

const AddEvent = ({plusDate, closeModal, setToast, eventCount, event}:AddEventProps) => {
    const [formValid, setFormValid] = useState<boolean>(false);
    const [showCal, setShowCal] = useState<boolean>(false);
    const [calDate, setCalDate] = useState<Date[]>([]);
    const [allDay, setAllDay] = useState<boolean>(false);
    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [approvalRequired, setApprovalRequired] = useState<boolean>(false);
    const [allowCustomTime, setAllowCustomTime] = useState<boolean>(false);
    const [maximumCapacity, setMaximumCapacity] = useState<boolean>(false);
    const [maximumCapacityCount, setMaximumCapacityCount] = useState<number>(1);
    const [maxCapTypes, setMaxCapTypes] = useState<'attendees' | 'hours'>('attendees');
    const [allowAttendeeResponseUpdates, setAllowAttendeeResponseUpdates] = useState<boolean>(true)
    const [location, setLocation] = useState<string>("");
    const [resetTime, setResetTime] = useState<boolean>(false);
    const [confirmModal, setConfirmModal] = useState<boolean>(false);
    const [startTime, setStartTime] = useState<string>(""); 
    const [endTime, setEndTime] = useState<string>("");
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [formTouched, setFormTouched] = useState<boolean>(false);
    const [showMaxCount, setShowMaxCount] = useState<boolean>(false);

    // Invitees 
    const [selectedGroups, setSelectedGroups] = useState<group[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<user[]>([]);

    const calOpenRef = useRef<boolean>(false);
    const newCal = useRef<boolean>(false);
    const saving = useRef<boolean>(false);
    const showArchiveModal = useRef<boolean>(false);

    const {selectedCal, getEvents, minimumEventTime} = useContext(PlannerContext)

    const setOnLoadRef = useRef<boolean>(false);


    const { post, put } = useHTTP();

    useEffect(() => {
        // Stop body scroll when in modal
        const body = document.querySelector("body")
        if (!body) return

        body.style.cssText = 'height: 100%;overflow-y: hidden;'

        // Reset saving
        saving.current = false;

        // Default times
        const now = new Date();

        setStartTime(getStartTime())
        setEndTime(getEndTime())

        function padZero(hours:number){

            if (hours.toString().length > 1){
                return hours.toString()
            }

            return `0${hours.toString()}`
        }

        function getStartTime(){
            if(now.getHours() + 1 > 23){
                return `${now.getHours()}:${padZero(now.getMinutes())}`
            }
            else{
                return `${padZero(now.getHours() + 1)}:00`
            }
        }

        function getEndTime(){
            if(now.getHours() + 2 >= 24){
                return '23:59'
            }
            else{
                return `${padZero(now.getHours() + 2)}:00`
            }
        }

        return () => {
            body.style.cssText = ''
        }

    }, [])

    // View - edit event?
    useEffect(() => {

        if (event){   
            
            // If start end same day then just push one date
            if (datesSame(event.occurrence.start, event.occurrence.end)){
                setOnLoadRef.current =  true
                setCalDate([event.occurrence.start])
            }
            else{
                setOnLoadRef.current =  true
                setCalDate([event.occurrence.start, event.occurrence.end])
            }
            
            // Times
            if ([occurrenceType.StartEnd, occurrenceType.StartEndConfigurable].includes(event.occurrence.occurrenceType)){

                const start = `${padZeros(event.occurrence.start.getHours())}:${padZeros(event.occurrence.start.getMinutes())}`;
                setStartTime(start)

                const end = `${padZeros(event.occurrence.end.getHours())}:${padZeros(event.occurrence.end.getMinutes())}`;
                setEndTime(end)
            }

            if ([occurrenceType.StartEndConfigurable, occurrenceType.AllDayConfigurable].includes(event.occurrence.occurrenceType)) {
                setAllowCustomTime(true);
            }
            
            if(event.maximumCapacity) {
                setMaximumCapacity(true);
                setMaximumCapacityCount(event.maximumCapacity);
            }


            if(event.workflowType === WorkflowType.SingleApprovalWorkflow) {
                setApprovalRequired(true);
            }
            
            

            setTitle(event.title)
            setDescription(event.description)
            setLocation(event.location.name)
            setAllDay( [occurrenceType.AllDay,occurrenceType.AllDayConfigurable].includes(event.occurrence.occurrenceType))
            setReadOnly(true)
            setAllowAttendeeResponseUpdates(event.allowAttendeesResponseUpdates)
            
        }

    }, [event]) 

    useEffect(() => {

        if(!setOnLoadRef.current) {
            unmountTime();
        }
        // If dates has changed then reset time
        setOnLoadRef.current = false;
        
    }, [calDate])

    // Plus date from day selected
    useEffect(() => {

        if (event) return

        if (plusDate){
            setCalDate([plusDate])
        }
        else{
            setCalDate([new Date()])
        }

    }, [plusDate, event])

    // Validate form on-going
    useEffect(() => {
        if(isGreaterThan24Hours() && allowCustomTime) {
            setFormValid(false);
        }

        else if (title.trim() && (selectedGroups.length || selectedUsers.length) && (allDay || isValidStartEndTime(startTime, endTime, calDate)) && isValidCapactiy() && !isLessThanMinimumTime()){
            setFormValid(true)
        } 
        else{
            setFormValid(false)
        }

    }, [title, selectedGroups, selectedUsers, startTime, endTime, allDay, maximumCapacityCount, allowCustomTime, calDate])

    useDetectClick("input input-cal", (clickedInCal) => {
        
        if (clickedInCal && !calOpenRef.current){ 
            setShowCal(true)
            calOpenRef.current = true;
            newCal.current = true;
        }
        else if (!clickedInCal && calOpenRef.current){ 
            closeCal()
        }
    });

    useEffect(() => {
        if(!event){
            if(approvalRequired) {
            setAllowAttendeeResponseUpdates(false);
            }
            if(!approvalRequired) {
                setAllowAttendeeResponseUpdates(true);
            }
        }  
    },[approvalRequired])

    useEffect(() => {
        if(!maximumCapacity) {
            setMaximumCapacityCount(1);
        }
    }, [maximumCapacity])

    function closeCal(){
        setShowCal(false)
        calOpenRef.current = false;
        newCal.current = false;
    }

    function save(){

        if (!formValid || saving.current){
            return 
        }

        saving.current = true;

        post(`/planner/calendar/${selectedCal.value}/events/create`, buildEvent()).then(() => {

            setToast({
                type: ToastMessageType.SUCCESS,
                message: 'Event added to planner',
                show: true
            })

        }).catch(() => {

            setToast({
                type: ToastMessageType.WARNING,
                message: 'Failed to save event',
                show: true
            })
        }).finally(() => {
            closeModal()
        })
    }

    function buildEvent(){

        function occurrenceBuilder(){

            const start = calDate[0];
            const end = calDate[1] ? calDate[1] : calDate[0];

            let occurence: any = {
                "occurrenceType": allDay ? 'allDay' : 'startEnd',
                "start": `${buildDateString(start)}${buildTimeString(true, start)}`,
                "end": `${buildDateString(end)}${buildTimeString(false, end)}`,
            }

            if (allowCustomTime) {
                occurence = {
                    ...occurence,
                    occurrenceType: allDay ? 'allDayConfigurable' : 'startEndConfigurable',
                }
            }

            return occurence
        }

        function buildDateString(date:Date){

            return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
        }

        function buildTimeString(start:boolean, date:Date){

            const [timeZone] = date.toString().match(/([-+][0-9]+)\s/);

            if (allDay){
                return `T00:00:00.000${timeZone.trim()}`      
            }
            else{
                return `T${start ? startTime : endTime}:00.000${timeZone.trim()}`  
            }     
        }

        function maxCapacityBuilder() {
            if(!maximumCapacity) {return undefined}

            return {
                maximumCapacityType:  maxCapTypes,
                maximumCapacityValue: maximumCapacityCount
            }
        }

        return {
            "event": {
                "title": title.trim(),
                "location": location.trim(),
                "description": description.trim(),
                "occurrence": occurrenceBuilder(),
                "invitees": {
                    "groups": selectedGroups.map(x => x.id),
                    "users": selectedUsers.map(x => x.id),
                },
                "maximumCapacity": maxCapacityBuilder(),
                'allowAttendeesResponseUpdates': approvalRequired ? false : allowAttendeeResponseUpdates,
                "workflowType": approvalRequired ?  'singleApproval' : 'noApproval'
            }
        }
    }

    function cancel(){
        closeModal();
        setConfirmModal(false);
    }

    function dateClick(date:Date){

        if (newCal.current){
            // If new cal ignore old values
            newCal.current = false;
            setCalDate([date])
        }
        else{   
            // Append with sort
            let sorted = [...calDate, date].sort((a,b) => a.getTime() - b.getTime())
  
            // Close on two values
            if (sorted.length >= 2){

                // If same date twice set to one and close
                if (datesSame(sorted[0], sorted[1])){
                    sorted = [sorted[0]]
                }

                // Get past click 
                setTimeout(() => {
                    closeCal()
                }, 200);
            }

            setCalDate(sorted)
        }
    }

    function formatDate(){

        // One Day
        if (calDate.length === 1){
            return formatString(calDate[0]);
        }
        // Two days
        else if (calDate.length === 2){
            return `${formatString(calDate[0])} - ${formatString(calDate[1])}`; 
        }

        return ""

        function formatString(date:Date){
            return Intl.DateTimeFormat(
                undefined, 
                {
                  year: "numeric", 
                  month: "2-digit", 
                  day: "2-digit"
                }).format(date)
        }
    }

    function unmountTime(){

        setResetTime(true)

        setTimeout(() => {
            setResetTime(false)  
        }, 200);
    }

    function closeProcess(){

        if(formTouched){
            setConfirmModal(true)
        }
        else{
            cancel()
        }
    }

    function isGreaterThan24Hours(){

        if (calDate.length > 1) {
            let st = startTime ? startTime : '00:00'
            let et = endTime ? endTime : '23:59'

            const start = new Date(calDate[0].getFullYear(), calDate[0].getMonth(), calDate[0].getDate(), +st.split(':')[0], +st.split(':')[1]);
            const end = new Date(calDate[1].getFullYear(), calDate[1].getMonth(), calDate[1].getDate(), +et.split(':')[0], +et.split(':')[1]);
            return ((end.getTime() - start.getTime()) / (1000 * 3600 * 24)) > 1;
        }
        
        return false;
     
    }

    function isValidCapactiy(){
        if (maximumCapacity) {
            return maximumCapacityCount > 0 && maximumCapacityCount < 1000
        }

        return true
    }
 
    if (!selectedCal){
        return null
    }

    function isLessThanMinimumTime() {

        if(!allowCustomTime) {
            return false
        }

        if(allDay) {
            return false    
        }
        
        if (calDate.length) {
            const startDate: Date = calDate[0]
            const endDate: Date = calDate.length > 1 ? calDate[1] : calDate[0]
            let st = startTime ? startTime : '00:00'
            let et = endTime ? endTime : '23:59'

            const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), +st.split(':')[0], +st.split(':')[1]);
            const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), +et.split(':')[0], +et.split(':')[1]);
    
            return end.getTime() - start.getTime() < minimumEventTime * 60000;
        }
        return false;
        
        
    }

    return (
        <SimpleBar style={{ maxHeight: '80vh', height: '695px', width: '757px', maxWidth: '90vw' }}>
            <div className='add-event-container'>
     
            
                <div className='form-container'>
                    {<div className={`validation-notification-wrapper ${isGreaterThan24Hours() && allowCustomTime ? 'show' : ''}`}>
                        <ThriveIcon sizeInPx='14' color='var(--thriveRed)' className='alert-icon' iconName={IconLibrary.ALERT_INFO} />
                        <span>Events longer than 24 hours cannot have 'Allow Custom Time Response' enabled</span>
                    </div>}

                    <InputCount 
                        label="Event Name" 
                        placeholder='Enter Title' 
                        max={140} 
                        warning={120} 
                        set={setTitle} 
                        value={title} 
                        required={true} 
                        readOnly={readOnly} 
                        setFormTouched={setFormTouched} 
                    />

                    <div className='input-container'>

                        <label className='input-label' htmlFor="date">DATE</label>

                        <div className='input input-cal' onClick={() => setFormTouched(true)}>

                            <input id="date" readOnly={true} className='input-field date' value={formatDate()} type='text' />

                            <ThriveIcon 
                                className="input-icon"
                                iconName={IconLibrary.CALENDAR}
                                sizeInPx='12'
                                color="var(--thriveGreyDark)"
                            />

                            {showCal && !readOnly &&
                                <div className='plan-mini-calendar' >
                                    <Calendar 
                                        minDate={new Date()}
                                        onClickDay={dateClick}
                                        showWeekNumbers={true} 
                                        showNeighboringMonth={false}
                                        selectRange={true}
                                        maxDetail="month" 
                                        minDetail="month" 
                                        prev2Label=""
                                        next2Label="" 
                                    />
                                </div>
                            }

                        </div>
                    </div>

                    <div className='input-container'>

                        <label className='input-label' htmlFor="allDay">TIME</label>
                    
                        <div className='input row-container time-container' onClick={() => setFormTouched(true)}>
                                
                            <Toggle
                                id="allDay"
                                className="react-toggle"
                                disabled={readOnly}
                                checked={allDay}
                                icons={false}
                                onChange={(e) => setAllDay(e.target.checked) } />

                                <span className='label'>All day event</span>

                                {!resetTime && 
                                <>
                                    <AnimatePresence>
                                        {!allDay && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <EventTime
                                                    startTime={startTime}
                                                    setStartTime={setStartTime}
                                                    setEndTime={setEndTime}
                                                    endTime={endTime}
                                                    readOnly={readOnly}
                                                    calDate={calDate}
                                                    event={event}
                                                />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    {isLessThanMinimumTime() && 
                                        <span style={{color:'#9a1d1c', fontSize: '12px', fontWeight: 600, marginLeft: '5px'}}>min: {minimumEventTime} minutes</span>
                                    }
                                </> 
                                }
                                

                        </div>
                    </div>

                    <InputCount 
                        label="Location" 
                        placeholder='Enter a Location' 
                        max={140} 
                        warning={120} 
                        set={setLocation} 
                        value={location}
                        icon="location.svg" 
                        readOnly={readOnly}
                        setFormTouched={setFormTouched}  />

                    <InputCount 
                        label="Description" 
                        placeholder='Add a description' 
                        max={2000} 
                        warning={1900} 
                        set={setDescription} 
                        value={description} 
                        textarea={true}  
                        readOnly={readOnly} 
                        setFormTouched={setFormTouched} />

                    <div className='input-container'>

                        <div className='input row-container' onClick={() => setFormTouched(true)}>
                                
                            <Toggle
                                id="approvalRequired"
                                className="react-toggle"
                                disabled={readOnly}
                                checked={approvalRequired}
                                icons={false}
                                onChange={(e) => {
                                    setApprovalRequired(e.target.checked)
                                }} />

                                <label htmlFor={'approvalRequired'} className='label'>Approval Required</label>

                            <ToolTipWrapper id="approval-required-tooltip" className="approval-required-tooltip" >                     
                                <ReactTooltip place="top" className='thrive-tooltip event-modal-tt' delayHide={1000}  id="approval-required-tooltip" >
                                    Attendee responses will need to be approved or denied by an admin
                                </ReactTooltip>
                            </ToolTipWrapper>
                            
                    </div>
                </div>


                    <div className='input-container'>

                        <div className='input row-container' onClick={() => setFormTouched(true)}>
                                
                            <Toggle
                                id="customTime"
                                className="react-toggle"
                                disabled={readOnly}
                                checked={allowCustomTime}
                                icons={false}
                                onChange={(e) => {
                                    setAllowCustomTime(e.target.checked)
                                    setMaxCapTypes(e.target.checked ? 'hours' : 'attendees')
                                    setMaximumCapacityCount(1)
                                }} />

                                <label htmlFor={'customTime'} className='label'>Allow Custom Time Response</label>

                            <ToolTipWrapper id="custom-time-tooltip" className="custom-time-tooltip" >                     
                                <ReactTooltip place="top" className='thrive-tooltip event-modal-tt' delayHide={1000}  id="custom-time-tooltip" >
                                    <span>Allow attendees to submit a custom start and end time within the defined hours</span>
                                </ReactTooltip>
                            </ToolTipWrapper>
                            
                    </div>
                </div>

                    <div className='input-container'>

                        <div className='input row-container'>
                                
                            <Toggle
                                id="maximumCapacity"
                                className="react-toggle"
                                disabled={readOnly}
                                checked={maximumCapacity}
                                icons={false}
                                onChange={(e) => setMaximumCapacity(e.target.checked) } 
                            />

                            <label htmlFor={'maximumCapacity'} className='label'>Maximum Capacity</label>

                            <AnimatePresence>
                                {maximumCapacity && (
                                    <motion.div
                                        className={'fade-container relative'}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <input type="number"
                                               min={1}
                                               max={999}
                                               className={`input-field input-max-capacity ${!isValidCapactiy() ? 'error' : ''}` }
                                               value={maximumCapacityCount}
                                               onChange={e => setMaximumCapacityCount(parseInt(e.target.value))}
                                               onFocus={() => {
                                                   setFormTouched(true)
                                                   setShowMaxCount(true)
                                               }}
                                               onBlur={() => {
                                                   setFormTouched(false)
                                                   setShowMaxCount(false)
                                               }}
                                               disabled={readOnly}
                                               aria-label={`maximum ${allowCustomTime ? 'hours' : 'attendees'}`}
                                        />
                                        <span className='label'>{allowCustomTime ? '(hours)' : '(attendees)'}</span>
                                        {(showMaxCount || !isValidCapactiy()) &&
                                            <div className={`maxcap-limit ${!isValidCapactiy() ? 'error' : ''}`}>
                                                1 - 999
                                            </div>}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <ToolTipWrapper id="max-capacity-tooltip" className="max-capacity-tooltip" >
                                <ReactTooltip place="top" className='thrive-tooltip event-modal-tt' delayHide={1000}  id="max-capacity-tooltip" >
                                    <span>Set a limit for how many attendees, or hours, are available for this event</span>
                                </ReactTooltip>
                            </ToolTipWrapper>

                        </div>
                    </div>

                    <div className='input-container'>

                        <div className='input row-container' >
                                
                            <Toggle
                                id="allowResponse"
                                className="react-toggle"
                                disabled={readOnly || approvalRequired}
                                checked={allowAttendeeResponseUpdates}
                                icons={false}
                                onChange={(e) => setAllowAttendeeResponseUpdates(e.target.checked) }
                            />

                            <label htmlFor={"allowResponse"} className='label'>Allow Attendee Response Updates</label>

                            <ToolTipWrapper id="attendee-response-tooltip" className="attendee-response-tooltip" >                     
                                <ReactTooltip place="top" className='thrive-tooltip event-modal-tt' delayHide={1000}  id="attendee-response-tooltip" >
                                    <span>This controls whether users can update their RSVP once submitted</span>
                                </ReactTooltip>
                            </ToolTipWrapper>
                        </div>

                        <span style={{fontSize: '12px', color: '#656b72', height:'37px', display:'block'}}>
                            {approvalRequired  
                            && 'When ‘Approval Required’ is enabled, users can update their RSVP until their RSVP has been actioned by a Planner Editor'
                            }
                        </span>
                    </div>
                </div>  

                <div className='invite-container'>
                    
                    {!event ? 
                        <>
                            <InviteesAdd setAllSelectedGroups={setSelectedGroups} setAllSelectedUsers={setSelectedUsers} setFormTouched={setFormTouched} calendarId={selectedCal.value} />

                            <div className={`controls ${formValid ? 'valid' : 'invalid'}`}>
                                <button className='cancel' onClick={() => closeProcess()}>Cancel</button>
                                <button className='save' onClick={save} disabled={!formValid}>Save Event</button>
                            </div> 
                        </>
                            : 
                        <>
                            <InviteesViewEdit 
                                eventCount={eventCount}
                                setAllSelectedUsers={setSelectedUsers}
                                event={event} 
                                isGatedEvent={event.workflowType === WorkflowType.SingleApprovalWorkflow}
                            />  

                            <div className={`controls ${formValid ? 'valid' : 'invalid'}`}>
                                <button className='cancel' onClick={() => cancel()}>Cancel</button>
                                <button className={`archive`} disabled={event.isCancelled} onClick={() => showArchiveModal.current = true}>Cancel Event</button>
                            </div> 
                        </>
                    }

                </div>

                <ModalWrapper className='confirm-cancel-event-modal' isOpen={confirmModal} escClose={false} onRequestClose={() => {}} hideCloseButton={true} >    
                    <div>
                        <h2>Do want to continue editing or disregard any unsaved changes?</h2>

                        <div className='controls'>
                            <button onClick={cancel}>Yes, Cancel</button>
                            <button className='continue' onClick={() => setConfirmModal(false)}>Continue editing</button>           
                        </div>     
                    </div>
                </ModalWrapper>

                <ModalWrapper className='archive-event-modal' isOpen={showArchiveModal.current} escClose={false}  onRequestClose={() => {}} hideCloseButton={true}>    
                    <p>Are you sure you want to archive this event? We will notify all attendees and invitees this event is now cancelled.</p>
                    <div className='archive-modal-button-wrapper'>
                        <button onClick={() => showArchiveModal.current = false}>Cancel</button>
                        <button className='archive' onClick={() => {
                            showArchiveModal.current = false
                            closeModal();
                            put(`planner/calendar/${selectedCal.value}/events/${event.id}/cancel`, {eventId: event.id})
                                .then(() => {
                                    setToast({type: ToastMessageType.SUCCESS, message: `${event.title} has been cancelled.`, show: true})
                                })
                                .catch(() => {
                                    setToast({type: ToastMessageType.WARNING, message: `${event.title} has not been cancelled.`, show: true})
                                })
                                .finally(() => {
                                    getEvents(selectedCal.value)
                                })

                        }}>Send Cancellation</button>
                    </div>
                </ModalWrapper>
           
        </div>
        </SimpleBar>
    );
};

export default AddEvent;
